import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"
import MainLayout from "../../layouts/MainLayout"
import { getMaisonDjoDashboard } from "../../services/maisonDjoDashboardService"
import { getBusinessLabel, getDashboardConfig } from "../../config/appConfig"


const DASHBOARD_SURFACE = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
  backgroundColor: "background.paper",
}

const DASHBOARD_FILTER_SURFACE = {
  ...DASHBOARD_SURFACE,
  p: { xs: 1.5, sm: 2 },
  mb: 2.5,
}

const DASHBOARD_SECTION_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
}

const MAIN_COMPANY = getBusinessLabel("mainCompany", "Entreprise principale")
const MAIN_COMPANY_SHORT = getBusinessLabel("mainCompanyShort", MAIN_COMPANY)
const MAIN_DISTRIBUTOR = getBusinessLabel("mainDistributor", "Distributeur principal")
const SHOP = getBusinessLabel("shop", "Shop")
const SHOPS = getBusinessLabel("shops", "Shops")
const COMPANY_PRICE = getBusinessLabel("companyPrice", "Prix entreprise")
const DISTRIBUTOR_PRICE = getBusinessLabel("distributorPrice", "Prix distributeur principal")
const SUPPLIER_DEBT = getBusinessLabel("supplierDebt", `Dette envers ${MAIN_COMPANY.toLowerCase()}`)

const DASHBOARD_CONFIG = getDashboardConfig("distributor")
const PAGE_TITLE = DASHBOARD_CONFIG?.label || `Dashboard ${MAIN_DISTRIBUTOR}`
const PAGE_DESCRIPTION = DASHBOARD_CONFIG?.description || `Suivi du ${MAIN_DISTRIBUTOR.toLowerCase()}, ventes, stock, paiements et rentabilité.`

const ORDER_STATUS_LABELS = {
  pending_maison_djo: `En attente ${MAIN_DISTRIBUTOR}`,
  pending_kin_mousse: `En attente ${MAIN_COMPANY}`,
  approved_by_kin_mousse: `Validée ${MAIN_COMPANY}`,
  shipped: "Expédiée",
  received_by_shop: `Reçue par le ${SHOP.toLowerCase()}`,
  rejected: "Rejetée",
  rejected_by_maison_djo: `Rejetée ${MAIN_DISTRIBUTOR}`,
  rejected_by_kin_mousse: `Rejetée ${MAIN_COMPANY}`,
  cancelled: "Annulée",
}

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

function money(value) {
  return toNumber(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function qty(value) {
  return toNumber(value).toLocaleString("fr-FR", { maximumFractionDigits: 2 })
}

function formatDate(value) {
  if (!value) return "-"
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("fr-FR")
}

function getApiErrorMessage(error) {
  return error?.response?.data?.detail || error?.message || `Erreur lors du chargement du dashboard ${MAIN_DISTRIBUTOR}.`
}

function KpiCard({ title, value, subtitle, tone = "default" }) {
  const color = tone === "success" ? "#15803d" : tone === "danger" ? "#dc2626" : tone === "warning" ? "#c2410c" : "#0f172a"

  return (
    <Card
      elevation={0}
      sx={{
        ...DASHBOARD_SURFACE,
        height: "100%",
        minHeight: 116,
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 34px rgba(15, 23, 42, 0.075)",
        },
      }}
    >
      <CardContent>
        <Typography variant="body2" color="text.secondary" fontWeight={700}>{title}</Typography>
        <Typography variant="h5" fontWeight={900} sx={{ mt: 1, color, wordBreak: "break-word" }}>{value}</Typography>
        {subtitle ? <Typography variant="caption" color="text.secondary">{subtitle}</Typography> : null}
      </CardContent>
    </Card>
  )
}

function SimpleTable({ title, columns, rows, emptyText, renderRow }) {
  return (
    <Paper elevation={0} sx={{ ...DASHBOARD_SURFACE, p: 2, overflow: "hidden" }}>
      <Typography variant="h6" fontWeight={900} mb={1.5}>{title}</Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 620 }}>
          <TableHead>
            <TableRow>{columns.map((column) => <TableCell key={column}>{column}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length}>{emptyText}</TableCell></TableRow>
            ) : rows.map(renderRow)}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  )
}

export default function MaisonDjoDashboardPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", store_id: "" })
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" })

  const stores = data?.filters?.stores || []
  const kpis = data?.kpis || {}
  const cashDisponible = useMemo(() => toNumber(kpis.total_paid) - toNumber(kpis.total_expenses), [kpis])


  const salesByShopChartData = useMemo(() => {
    return (data?.sales_by_store || [])
      .map((row) => ({
        name: row.store_name || row.store_code || SHOP,
        full_name:
          row.store_name && row.store_code
            ? `${row.store_name} (${row.store_code})`
            : row.store_name || row.store_code || SHOP,
        sales: toNumber(row.total_sales),
        paid: toNumber(row.total_paid),
        remaining: toNumber(row.total_remaining),
      }))
      .filter((row) => row.sales > 0 || row.paid > 0 || row.remaining > 0)
  }, [data?.sales_by_store])


  const topProductsChartData = useMemo(() => {
    return [...(data?.top_products || [])]
      .sort((a, b) => toNumber(b.quantity_sold) - toNumber(a.quantity_sold))
      .slice(0, 5)
      .map((row) => ({
        name: row.product_name || row.product_code || "Produit",
        full_name:
          row.product_name && row.product_code
            ? `${row.product_name} (${row.product_code})`
            : row.product_name || row.product_code || "Produit",
        quantity: toNumber(row.quantity_sold),
        sales: toNumber(row.total_sales),
        margin: toNumber(row.gross_margin),
      }))
  }, [data?.top_products])


  const financialBreakdownChartData = useMemo(() => {
    return [
      {
        name: "Marge brute",
        value: Math.max(0, toNumber(kpis.gross_margin)),
      },
      {
        name: "Dépenses",
        value: Math.max(0, toNumber(kpis.total_expenses)),
      },
      {
        name: "Reste client",
        value: Math.max(0, toNumber(kpis.total_remaining)),
      },
    ].filter((item) => item.value > 0)
  }, [kpis.gross_margin, kpis.total_expenses, kpis.total_remaining])


  const pieColors = [
    "#2e7d32",
    "#d32f2f",
    "#ed6c02",
  ]

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, severity, message })
  const closeSnackbar = () => setSnackbar((current) => ({ ...current, open: false }))

  const loadDashboard = async (nextFilters = filters) => {
    try {
      setLoading(true)
      const result = await getMaisonDjoDashboard(nextFilters)
      setData(result)
    } catch (error) {
      console.error(error)
      showSnackbar(getApiErrorMessage(error), "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadDashboard({ date_from: "", date_to: "", store_id: "" }) }, [])

  const handleChange = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({ ...current, [name]: value }))
  }

  const resetFilters = () => {
    const next = { date_from: "", date_to: "", store_id: "" }
    setFilters(next)
    loadDashboard(next)
  }

  return (
    <MainLayout>
      <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Box sx={{ mb: 2.25 }}>
          <Typography
            variant="overline"
            sx={{ color: "primary.main", fontWeight: 900, letterSpacing: 1.1 }}
          >
            PILOTAGE
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "flex-end" }}
            spacing={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={900}
                sx={{ fontSize: { xs: "1.65rem", sm: "1.95rem", md: "2.1rem" } }}
              >
                {PAGE_TITLE}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, maxWidth: 900 }}>
                {PAGE_DESCRIPTION}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadDashboard()}
              disabled={loading}
              sx={{ minHeight: 44, flexShrink: 0 }}
            >
              {loading ? "Chargement..." : "Actualiser"}
            </Button>
          </Stack>
        </Box>

        <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1.5fr auto auto" }, gap: 2, alignItems: "center" }}>
            <TextField label="Date début" name="date_from" type="date" value={filters.date_from} onChange={handleChange} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            <TextField label="Date fin" name="date_to" type="date" value={filters.date_to} onChange={handleChange} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            <TextField select label={SHOP} name="store_id" value={filters.store_id} onChange={handleChange} fullWidth disabled={data?.scope?.is_store_locked}>
              <MenuItem value="">Tous les {SHOPS.toLowerCase()}</MenuItem>
              {stores.map((store) => <MenuItem key={store.id} value={store.id}>{store.code} - {store.name}</MenuItem>)}
            </TextField>
            <Button variant="contained" onClick={() => loadDashboard()} disabled={loading}>Charger</Button>
            <Button variant="outlined" onClick={resetFilters} disabled={loading}>Reset</Button>
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 2, mb: 3 }}>
          <KpiCard title={`Ventes ${MAIN_DISTRIBUTOR}`} value={money(kpis.total_sales)} subtitle={`${qty(kpis.sales_count)} transaction(s)`} />
          <KpiCard title="Montant encaissé" value={money(kpis.total_paid)} subtitle="Cash reçu des clients" tone="success" />
          <KpiCard title="Reste client à payer" value={money(kpis.total_remaining)} subtitle="Créances clients" tone="warning" />
          <KpiCard title="Dépenses" value={money(kpis.total_expenses)} subtitle={`${qty(kpis.expenses_count)} dépense(s)`} tone="danger" />
          <KpiCard title={SUPPLIER_DEBT} value={money(kpis.debt_remaining)} subtitle={`Dette initiale : ${money(kpis.debt_original)}`} tone="danger" />
          <KpiCard title={`Déjà payé à ${MAIN_COMPANY_SHORT}`} value={money(kpis.debt_paid)} subtitle="Règlements confirmés" tone="success" />
          <KpiCard title="Marge brute estimée" value={money(kpis.gross_margin)} subtitle="Ventes - coût d'achat" tone="success" />
          <KpiCard title="Bénéfice net estimé" value={money(kpis.net_profit)} subtitle="Marge brute - dépenses" tone={toNumber(kpis.net_profit) >= 0 ? "success" : "danger"} />
          <KpiCard title="Stock total" value={qty(kpis.total_stock_quantity)} subtitle={`${qty(kpis.products_in_stock)} produit(s) en stock`} />
          <KpiCard title="Valeur stock achat" value={money(kpis.stock_purchase_value)} subtitle={`Valorisé au ${COMPANY_PRICE.toLowerCase()}`} />
          <KpiCard title="Valeur stock vente" value={money(kpis.stock_sale_value)} subtitle={`Valorisé au ${DISTRIBUTOR_PRICE.toLowerCase()}`} />
          <KpiCard title="Cash disponible estimé" value={money(cashDisponible)} subtitle="Encaissements - dépenses" tone={cashDisponible >= 0 ? "success" : "danger"} />
          <KpiCard title="Commandes stock" value={qty(kpis.stock_orders_total)} subtitle="Réapprovisionnement" />
          <KpiCard title={`En attente ${MAIN_COMPANY_SHORT}`} value={qty(kpis.stock_orders_pending_kin_mousse)} subtitle="À valider côté entreprise" tone="warning" />
          <KpiCard title="Expédiées / reçues" value={`${qty(kpis.stock_orders_shipped)} / ${qty(kpis.stock_orders_received)}`} subtitle="Livraisons" />
          <KpiCard title="Marge potentielle stock" value={money(kpis.stock_potential_margin)} subtitle="Si tout le stock est vendu" tone="success" />
        </Box>


        {/* =================================================
            GRAPHIQUES
            ================================================= */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              xl: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 3,
          }}
        >
          <Paper
            sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
          >
            <Typography variant="h6" fontWeight={900}>
              Ventes par {SHOP.toLowerCase()}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Comparaison des ventes, encaissements et restes par {SHOP.toLowerCase()}.
            </Typography>

            <Box sx={{ width: "100%", height: { xs: 300, sm: 330 } }}>
              {salesByShopChartData.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée disponible.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={salesByShopChartData}
                    margin={{ top: 10, right: 15, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={70}
                    />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        money(value),
                        name === "sales"
                          ? "Ventes"
                          : name === "paid"
                            ? "Payé"
                            : "Reste",
                      ]}
                      labelFormatter={(label, payload) =>
                        payload?.[0]?.payload?.full_name || label
                      }
                    />
                    <Legend />
                    <Bar
                      dataKey="sales"
                      name="Ventes"
                      fill="#1976d2"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={54}
                    />
                    <Bar
                      dataKey="paid"
                      name="Payé"
                      fill="#2e7d32"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={54}
                    />
                    <Bar
                      dataKey="remaining"
                      name="Reste"
                      fill="#ed6c02"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={54}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>


          <Paper
            sx={{ ...DASHBOARD_SURFACE, p: 2, minWidth: 0 }}
          >
            <Typography variant="h6" fontWeight={900}>
              Répartition financière
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Lecture synthétique de la marge brute, des dépenses et du reste client.
            </Typography>

            <Box sx={{ width: "100%", height: { xs: 300, sm: 330 } }}>
              {financialBreakdownChartData.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune valeur disponible.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={financialBreakdownChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={65}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {financialBreakdownChartData.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={pieColors[index % pieColors.length]}
                        />
                      ))}
                    </Pie>

                    <RechartsTooltip
                      formatter={(value) => [
                        money(value),
                        "Montant",
                      ]}
                    />

                    <Legend
                      verticalAlign="bottom"
                      height={42}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Box>


        <Paper
          sx={{ ...DASHBOARD_SURFACE, p: 2, mb: 3, minWidth: 0 }}
        >
          <Typography variant="h6" fontWeight={900}>
            Top 5 produits vendus
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Produits les plus vendus selon les quantités enregistrées.
          </Typography>

          <Box sx={{ width: "100%", height: { xs: 300, sm: 330 } }}>
            {topProductsChartData.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography color="text.secondary">
                  Aucun produit disponible.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topProductsChartData}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <RechartsTooltip
                    formatter={(value, name) => [
                      name === "quantity" ? qty(value) : money(value),
                      name === "quantity"
                        ? "Quantité"
                        : name === "sales"
                          ? "Ventes"
                          : "Marge",
                    ]}
                    labelFormatter={(label, payload) =>
                      payload?.[0]?.payload?.full_name || label
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="quantity"
                    name="Quantité"
                    fill="#1976d2"
                    radius={[0, 8, 8, 0]}
                    maxBarSize={28}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 2 }}>
          <SimpleTable title={`Ventes par ${SHOP.toLowerCase()}`} columns={[SHOP, "Ventes", "Payé", "Reste"]} rows={data?.sales_by_store || []} emptyText="Aucune donnée." renderRow={(row) => (
            <TableRow key={row.store_id}><TableCell><b>{row.store_code}</b> - {row.store_name}</TableCell><TableCell align="right">{money(row.total_sales)}</TableCell><TableCell align="right">{money(row.total_paid)}</TableCell><TableCell align="right">{money(row.total_remaining)}</TableCell></TableRow>
          )} />
          <SimpleTable title="Top produits vendus" columns={["Produit", "Qté", "Vente", "Marge"]} rows={data?.top_products || []} emptyText="Aucune donnée." renderRow={(row) => (
            <TableRow key={row.product_id}><TableCell><b>{row.product_code}</b> - {row.product_name}</TableCell><TableCell align="right">{qty(row.quantity_sold)}</TableCell><TableCell align="right">{money(row.total_sales)}</TableCell><TableCell align="right">{money(row.gross_margin)}</TableCell></TableRow>
          )} />
          <SimpleTable title="Stock faible" columns={[SHOP, "Produit", "Stock"]} rows={data?.low_stock || []} emptyText="Aucun stock faible." renderRow={(row, index) => (
            <TableRow key={`${row.store_id}-${row.product_id}-${index}`}><TableCell>{row.store_code} - {row.store_name}</TableCell><TableCell>{row.product_code} - {row.product_name}</TableCell><TableCell align="right"><Chip label={qty(row.quantity)} color="warning" size="small" /></TableCell></TableRow>
          )} />
          <SimpleTable title="Dernières commandes stock" columns={["Code", SHOP, "Statut", "Qté", "Dette", "Marge", "Date"]} rows={data?.recent_orders || []} emptyText="Aucune commande récente." renderRow={(row) => (
            <TableRow key={row.id}><TableCell>{row.order_code}</TableCell><TableCell>{row.store_code} - {row.store_name}</TableCell><TableCell><Chip label={ORDER_STATUS_LABELS[row.status] || row.status || "-"} size="small" /></TableCell><TableCell align="right">{qty(row.requested_quantity)}</TableCell><TableCell align="right">{money(row.supplier_total_amount)}</TableCell><TableCell align="right">{money(row.expected_margin)}</TableCell><TableCell>{formatDate(row.created_at)}</TableCell></TableRow>
          )} />
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={closeSnackbar}>{snackbar.message}</Alert>
      </Snackbar>
    </MainLayout>
  )
}